import { useCallback, useMemo, useRef, useState } from 'react';

const PHOTOS_KEY_SEPARATOR = ' ';
const NO_BROKEN_PHOTOS = [];

function clampIndex(index, count) {
    return count > 0 ? Math.min(Math.max(index || 0, 0), count - 1) : 0;
}

/**
 * The shown index after `url` leaves the list: the photo shown stays shown; if it is the one that broke, the next
 * survivor takes its slot (the last survivor when it was the last).
 */
export function indexAfterRemoval(photos, index, url) {
    const survivorsBefore = photos.slice(0, index).filter((photo) => photo !== url).length;
    const survivors = photos.filter((photo) => photo !== url).length;
    return clampIndex(survivorsBefore, survivors);
}

/**
 * One owner's photo list for an RM3 room: a URL whose `<img>` fails leaves the list and the shown photo stays shown;
 * `initialBroken` / `onBroken` carry failures between the card and its details views.
 */
function useRm3Photos(images, { startIndex = 0, initialBroken = NO_BROKEN_PHOTOS, onBroken } = {}) {
    const photosKey = images.join(PHOTOS_KEY_SEPARATOR);
    const latest = useRef({ images, photosKey });
    latest.current = { images, photosKey };
    const [state, setState] = useState(() => ({
        broken: new Set(initialBroken),
        shown: { key: photosKey, index: startIndex },
    }));

    const { broken } = state;
    const photos = useMemo(() => images.filter((url) => !broken.has(url)), [images, broken]);
    const index = clampIndex(state.shown.key === photosKey ? state.shown.index : 0, photos.length);

    const setIndex = useCallback((nextIndex) => {
        setState((previous) => ({ ...previous, shown: { key: latest.current.photosKey, index: nextIndex } }));
    }, []);

    const markBroken = useCallback(
        (url) => {
            setState((previous) => {
                if (previous.broken.has(url)) {
                    return previous;
                }
                const { images: list, photosKey: key } = latest.current;
                const before = list.filter((photo) => !previous.broken.has(photo));
                const shownIndex = clampIndex(previous.shown.key === key ? previous.shown.index : 0, before.length);
                return {
                    broken: new Set(previous.broken).add(url),
                    shown: { key, index: indexAfterRemoval(before, shownIndex, url) },
                };
            });
            if (onBroken) {
                onBroken(url);
            }
        },
        [onBroken],
    );

    return { photos, index, setIndex, markBroken, broken };
}

export default useRm3Photos;
